import * as THREE from 'three';
import { label } from './aquariumView';
import { FISH_LIVES, classesOf, monthStep, type FishSpeciesId } from '../sim/fish';
import { isReefSpecies, type TaggedAnimal, type Tags } from '../sim/tags';
import { HONU, stagesOn } from '../sim/turtles';
import { MONK_SEAL } from '../sim/seals';
import type { SiteId } from '../sim/data/sites';
import type { FishSchools } from './fishSchools';
import type { Pickable } from './pick';

/**
 * The tagged animals and the named schools marked in a view (M156, M148's split: the user's choice "marked in the
 * views"): each its name in gold above the school or the animal that stands for it, one size on the screen however far
 * the camera stands, as the ruler's labels are (`label`), drawn over the water and the fish.
 *
 * Each a rendering choice (JUDGED): `cssPx` its letters' size; `color` the gold the sanctuary's names are drawn in
 * elsewhere (the habitats' band names, `#ffe9a8`), a little warmer so a mark is not taken for a band's name.
 */
export const TAG_MARK = { cssPx: 13, color: '#ffd76a' } as const;

/** A mark to draw: its names and the point it stands over; none drawn where `at` is null; marks on one point drawn as one, their names together. */
export interface TagMark {
  text: string;
  at: THREE.Vector3 | null;
}

/**
 * What stands for a tag in a view: a reef stock's tagged fish or named school by a school of its kind on its island (its
 * young's in the Reef's scene, which draws them), a honu, a monk seal or a humpback by one of its kind drawn (the
 * simulation holds no animal's place, only its island or its sanctuary).
 */
export type MarkTarget =
  // A day octopus's tag its own mark (M201): its tag's id; and the drawn school or octopus it was given from, where one
  // was followed (`seenAs`, M203, kept with the save; the session's memory before).
  | { species: FishSpeciesId; island: number; young: boolean; tag?: number; seen?: number }
  // A honu, a hawksbill, a seal or a tiger shark its tag's id too (M203): over itself where a habitat draws it there.
  | { species: 'honu'; adult: boolean; tag?: number }
  | { species: 'monkSeal'; stage: 'pup' | 'juvenile' | 'adult'; tag?: number }
  | { species: 'honuea' | 'tigerShark'; tag: number }
  | { species: 'humpback' };

/**
 * The marks a sanctuary's tags call for (M156): each living tagged animal at home and each named group (a school over
 * its kind on its island, a pod over its first living member at home), one mark for all those standing on the same
 * target, their names together; none for one dead or away (a honu breeding at French Frigate Shoals, a humpback on its
 * feeding grounds).
 */
export function tagTargets(tags: Tags, siteId: SiteId, epochDay: number): { target: MarkTarget; text: string }[] {
  const by = new Map<string, { target: MarkTarget; names: string[] }>();
  const add = (target: MarkTarget, name: string): void => {
    const key = JSON.stringify(target);
    const at = by.get(key) ?? { target, names: [] };
    if (!at.names.includes(name)) at.names.push(name);
    by.set(key, at);
  };
  const home = tags.animals.filter((a) => a.siteId === siteId && !a.died && !a.away);
  for (const g of tags.groups) {
    if (g.siteId !== siteId) continue;
    if (g.kind === 'school' && g.island !== null && isReefSpecies(g.species)) add({ species: g.species, island: g.island, young: false, ...(g.seenAs !== null ? { seen: g.seenAs } : {}) }, g.name);
    const first = g.kind === 'pod' ? home.find((a) => a.groupId === g.id) : undefined;
    const target = first ? targetOf(first, epochDay) : null;
    if (target) add(target, g.name);
  }
  // A day octopus's tag its own mark (M201, the user's report: two tagged on one island shared one mark, and the name
  // moved to the octopus beside it): its tag's id.
  for (const a of home) {
    const target = targetOf(a, epochDay);
    if (target) add(target.species === 'octopus' && 'island' in target ? { ...target, tag: a.id } : target, a.name);
  }
  return [...by.values()].map((v) => ({ target: v.target, text: v.names.join(', ') }));
}

/**
 * What stands for a tagged animal: a reef fish its kind's school on its island, young only while every fish of its class
 * is young that month (the tag keeps no stage, and an adult is drawn from a class by its grown share, `tagAnimal`, so a
 * class with any grown is marked grown: M156's review, by the older rule of most of it young 50% of the bulletheads
 * tagged as adults were marked young); a honu its stage's honu (`stagesOn`), a seal its stage's (pups the year's,
 * adults from `firstPupYears`, as `sealStages` draws them), a humpback any grown one.
 */
function targetOf(a: TaggedAnimal, epochDay: number): MarkTarget | null {
  if (isReefSpecies(a.species)) {
    const life = FISH_LIVES.find((l) => l.id === a.species);
    const k = life ? classesOf(life)[monthStep(epochDay)]?.[a.cls] : undefined;
    const young = k ? k.young > 0 && k.adult + k.terminal === 0 : false;
    return a.island === null ? null : { species: a.species, island: a.island, young, ...(a.seenAs !== null ? { seen: a.seenAs } : {}) };
  }
  if (a.species === 'honu') return { species: 'honu', adult: stagesOn(HONU, epochDay)[a.cls] === 'adult', tag: a.id };
  if (a.species === 'monkSeal') return { species: 'monkSeal', stage: a.cls === 0 ? 'pup' : a.cls < MONK_SEAL.firstPupYears ? 'juvenile' : 'adult', tag: a.id };
  if (a.species === 'honuea' || a.species === 'tigerShark') return { species: a.species, tag: a.id };
  return { species: 'humpback' };
}

/** Where a tagged visitor's mark stands in a habitat's scene (M203): over its own drawn visitor (`taggedVisitorsOf`) while it is drawn, fading in or out included (M201's third review, of the octopus's); null for any other. */
export function visitorMark(target: MarkTarget, visitors: { drawnOf(id: string): { middle: THREE.Vector3; alpha: number } | null }): THREE.Vector3 | null {
  const tag = 'tag' in target && !('island' in target) ? target.tag : undefined;
  if (tag === undefined) return null;
  const v = visitors.drawnOf(`tag:${tag}`);
  return v && v.alpha > 0 ? v.middle.clone() : null;
}

/** What a mark's target was given from: the drawn school or octopus its tag or group names (`seenAs`, M203), or none. */
export function rememberedFor(target: MarkTarget): number | null {
  return 'island' in target ? target.seen ?? null : null;
}

/**
 * A school's mark: over the school of a kind on an island the player tagged from where it is still drawn (`remembered`,
 * its tag's or group's `seenAs`, M203), else its lowest-numbered school there, the same each time; null where
 * the view draws none of that kind there.
 */
export function schoolMark(fish: Pick<FishSchools, 'seedsOf' | 'schoolAt'>, kind: number, island: number, remembered: number | null): THREE.Vector3 | null {
  const seeds = fish.seedsOf(kind, island);
  const seed = remembered !== null && seeds.includes(remembered) ? remembered : seeds[0];
  return seed === undefined ? null : fish.schoolAt(seed)?.middle ?? null;
}

/**
 * Which of the animals of a kind drawn stands for a tag in a view (M161, the user's report: "the tagged humpback whale
 * doesn't show its tagged name at certain angles until the camera is rotated"): the one it stood over last while the
 * camera still sees it (`seen`), else the lowest-numbered seen, else the lowest-numbered shown more than half (M156's
 * review: a mark over a sprite fading out); null where none is. By M156's rule alone it stood over the lowest-numbered
 * wherever that one was, behind the camera or off the screen with others in view.
 */
export function standIn<T extends Pickable>(drawn: readonly T[], n: (d: T) => number, seen: (d: T) => boolean, last: number | undefined): T | null {
  const shown = drawn.filter((d) => d.alpha > 0.5).sort((a, b) => n(a) - n(b));
  const inView = shown.filter(seen);
  return inView.find((d) => n(d) === last) ?? inView[0] ?? shown[0] ?? null;
}

/** A view's marks: a label for each, kept while its words are the same, freed when gone. */
export class TagMarks {
  readonly group = new THREE.Group();
  private readonly labels = new Map<string, THREE.Sprite>();

  /**
   * The marks now, sized for `camera` on a canvas `canvasPx` high (`fitLabels`' rule: its letters `cssPx` high whatever
   * the distance): those standing on one point drawn as one, their names together (M156's review: a view drawing no young
   * school put a grown and a young tag on one school, their two labels over one another); two alike in words on two
   * points each drawn (its review: the second was dropped).
   */
  set(marks: readonly TagMark[], camera: THREE.Camera, canvasPx: number): void {
    const p11 = camera.projectionMatrix.elements[5] ?? 1;
    const points: { text: string; at: THREE.Vector3 }[] = [];
    for (const m of marks) {
      if (!m.at) continue;
      const on = points.find((p) => p.at.distanceToSquared(m.at as THREE.Vector3) < 1e-12);
      if (on) on.text = [...new Set([...on.text.split(', '), ...m.text.split(', ')])].join(', ');
      else points.push({ text: m.text, at: m.at });
    }
    const kept = new Set<string>();
    for (const m of points) {
      let key = m.text;
      for (let n = 2; kept.has(key); n++) key = `${m.text}#${n}`;
      kept.add(key);
      let s = this.labels.get(key);
      if (!s) {
        s = label(m.text, TAG_MARK.cssPx, TAG_MARK.color);
        // Its point may be off the screen while the label, drawn above it, is on it: never culled by its point alone.
        s.frustumCulled = false;
        // Standing over the point, its foot one and a half of its own heights above it, clear of a fish drawn as large as
        // the Reef's nearest (MEASURED in the built game: two-thirds of its height left the name across a roi's body).
        s.center.set(0.5, -1.5);
        this.labels.set(key, s);
        this.group.add(s);
      }
      s.position.copy(m.at);
      const h = (2 * (s.userData.labelPx as number)) / Math.max(1e-9, p11 * canvasPx);
      s.scale.set(h * (s.userData.aspect as number), h, 1);
    }
    for (const [key, s] of this.labels) {
      if (kept.has(key)) continue;
      this.free(s);
      this.labels.delete(key);
    }
  }

  /** The marks drawn now, their words and where they stand, for the tests. */
  shown(): { text: string; at: THREE.Vector3 }[] {
    return [...this.labels.values()].map((s) => ({ text: s.userData.text as string, at: s.position.clone() }));
  }

  private free(s: THREE.Sprite): void {
    this.group.remove(s);
    s.material.map?.dispose();
    s.material.dispose();
  }

  dispose(): void {
    for (const s of this.labels.values()) this.free(s);
    this.labels.clear();
  }
}
